import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, Check, AlertCircle, Clock, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WorkAuthorizationCardProps {
  workAuthorization?: string | null;
  mode?: 'candidate' | 'employer';
  medicalClass?: string | null;
  className?: string;
}

const authorizationConfig: Record<string, { label: string; icon: React.ReactNode; color: string; description: string }> = {
  'citizen': { 
    label: 'Citizen', 
    icon: <Check className="w-4 h-4" />, 
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400',
    description: 'Full work authorization - no sponsorship required'
  },
  'permanent_resident': { 
    label: 'Permanent Resident', 
    icon: <Check className="w-4 h-4" />, 
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400',
    description: 'Full work authorization - no sponsorship required'
  },
  'work_visa': { 
    label: 'Work Visa', 
    icon: <Globe className="w-4 h-4" />, 
    color: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400',
    description: 'Currently holds valid work visa'
  },
  'visa_required': { 
    label: 'Visa Sponsorship Required', 
    icon: <AlertCircle className="w-4 h-4" />, 
    color: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400',
    description: 'Will require visa sponsorship'
  },
  'student_visa': { 
    label: 'Student Visa (OPT/CPT)', 
    icon: <Clock className="w-4 h-4" />, 
    color: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400',
    description: 'Limited work authorization through student visa'
  },
  // Employer requirements
  'no_sponsorship': { 
    label: 'No Sponsorship', 
    icon: <Check className="w-4 h-4" />, 
    color: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300',
    description: 'Candidates must have work authorization'
  },
  'will_sponsor': { 
    label: 'Will Sponsor', 
    icon: <Globe className="w-4 h-4" />, 
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400',
    description: 'Company will sponsor work visas'
  },
};

const medicalClassConfig: Record<string, { label: string; color: string }> = {
  'class_1': { label: 'Class 1 Medical', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  'class_2': { label: 'Class 2 Medical', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  'class_3': { label: 'Class 3 Medical', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
};

export const WorkAuthorizationCard: React.FC<WorkAuthorizationCardProps> = ({
  workAuthorization,
  mode = 'candidate',
  medicalClass,
  className
}) => {
  if (!workAuthorization && !medicalClass) return null;

  const authConfig = workAuthorization ? authorizationConfig[workAuthorization] : null;
  const medConfig = medicalClass ? medicalClassConfig[medicalClass] : null;

  return (
    <Card className={cn("border-border", className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Shield className="w-4 h-4 text-primary" />
          {mode === 'employer' ? 'Authorization Requirements' : 'Work Authorization'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {authConfig && (
          <div className="flex items-start gap-3">
            <Badge 
              variant="outline" 
              className={cn("gap-1.5 py-1.5 px-3", authConfig.color)}
            >
              {authConfig.icon}
              {authConfig.label}
            </Badge>
          </div>
        )}
        
        {authConfig && (
          <p className="text-xs text-muted-foreground">{authConfig.description}</p>
        )}

        {medConfig && (
          <div className="pt-2 border-t border-border">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Medical Certificate</span>
            </div>
            <Badge 
              variant="outline" 
              className={cn("gap-1.5", medConfig.color)}
            >
              <Check className="w-3 h-3" />
              {medConfig.label}
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkAuthorizationCard;
